import type { JustificatifEntrant } from '../../types/ndf';

/**
 * Compression des photos AVANT envoi.
 *
 * Une photo d'iPhone pèse 3 à 5 Mo. Le serveur ne la refuserait pas — les 10 Mo
 * ne concernent que les factures (règle de Valérie du 07/10/2026) —, mais le
 * vrai problème est ailleurs : en 4G sur un quai de gare, envoyer 4 Mo ruine
 * l'objectif des trente secondes. Redimensionnée à 2000 px en JPEG 0,8, la
 * même photo tombe autour de 400 Ko — largement assez pour lire un ticket.
 *
 * La compression produit une URL `data:`, ce qui règle au passage l'aperçu :
 * la politique de sécurité de l'application n'autorise pas `blob:`.
 */

const COTE_MAX = 2000;
const QUALITE = 0.8;

/** Les PDF passent tels quels : les recomprimer ferait perdre du texte. */
function estImage(fichier: File) {
  return fichier.type.startsWith('image/');
}

function lireEnDataUrl(fichier: File): Promise<string> {
  return new Promise((resoudre, rejeter) => {
    const lecteur = new FileReader();
    lecteur.onload = () => resoudre(String(lecteur.result));
    lecteur.onerror = () => rejeter(new Error('Fichier illisible'));
    lecteur.readAsDataURL(fichier);
  });
}

function base64De(dataUrl: string): string {
  const i = dataUrl.indexOf(',');
  return i >= 0 ? dataUrl.slice(i + 1) : dataUrl;
}

export async function preparerJustificatif(fichier: File): Promise<JustificatifEntrant> {
  const dataUrl = await lireEnDataUrl(fichier);

  if (!estImage(fichier)) {
    return {
      filename: fichier.name,
      contenu_base64: base64De(dataUrl),
      mime: fichier.type || 'application/pdf',
    };
  }

  // L'iPhone photographie parfois en HEIC, que tous les navigateurs ne savent
  // pas décoder. En cas d'échec, on envoie la photo TELLE QUELLE plutôt que de
  // la refuser : un envoi plus lourd vaut mieux qu'un justificatif perdu — le
  // serveur, lui, sait la convertir.
  const image = new Image();
  const decodable = await new Promise<boolean>((resoudre) => {
    image.onload = () => resoudre(true);
    image.onerror = () => resoudre(false);
    image.src = dataUrl;
  });
  if (!decodable) {
    return { filename: fichier.name, contenu_base64: base64De(dataUrl), mime: fichier.type };
  }

  const facteur = Math.min(1, COTE_MAX / Math.max(image.width, image.height));
  const largeur = Math.round(image.width * facteur);
  const hauteur = Math.round(image.height * facteur);

  const toile = document.createElement('canvas');
  toile.width = largeur;
  toile.height = hauteur;
  const contexte = toile.getContext('2d');
  if (!contexte) {
    // Sans canvas, on envoie l'original : mieux vaut un envoi lent qu'une
    // photo perdue.
    return { filename: fichier.name, contenu_base64: base64De(dataUrl), mime: fichier.type };
  }
  contexte.drawImage(image, 0, 0, largeur, hauteur);

  return {
    filename: fichier.name.replace(/\.[^.]+$/, '') + '.jpg',
    contenu_base64: base64De(toile.toDataURL('image/jpeg', QUALITE)),
    mime: 'image/jpeg',
  };
}

/** Aperçu affichable sans passer par `blob:`, bloqué par la politique de sécurité. */
export function apercuDe(j: JustificatifEntrant): string {
  return `data:${j.mime};base64,${j.contenu_base64}`;
}
